import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import PropTypes from "prop-types";
import ImmutablePropTypes from "react-immutable-proptypes";
import Immutable from "immutable";
import classNames from "classnames";
import { getResource } from "selectors/resources";
import { Client } from "resourceModels";
import DebouncedInput from "components/Common/DebouncedInput";
import Button from "components/Common/Button";
import Select from "components/Common/Select";
import SvgIcon from "components/Common/SvgIcon";
import ModalNotification from "components/Common/ModalNotification";
import { noticeUrl } from "components/Pages/Settings/SettingsAuthentication/index";
import {
  postAuthentication,
  patchAuthentication,
  handleUpdateAuthenticationModal,
  handleAddAuthenticationScope,
  handleRemoveAuthenticationScope,
  postCertificate,
  handleRemoveAuthenticationCertificate,
  handleUpdateAuthenticationScope,
  validateAutenticationModal,
  updateOrCreateAuthentication
} from "components/Pages/Settings/SettingsAuthentication/actions";
import {
  AUTH_TYPE_OAUTH,
  AUTH_TYPE_JWT
} from "components/Pages/Settings/SettingsAuthentication/reducer";
import colors from "stylesheets/utilities/colors.scss";
import "./style.scss";

const authOptions = [
  {
    label: "OAuth 2",
    value: AUTH_TYPE_OAUTH
  },
  {
    label: "JWT",
    value: AUTH_TYPE_JWT
  }
];

const noticeMessage = "Need help? Click here to read our guide on how to set up authentication!";
const notice = {
  url: noticeUrl,
  message: noticeMessage
};

class AuthenticationModal extends React.Component {
  /**
   * split filename string into filename and extension
   * @param {String} fullFilename
   * @returns {Object}
   */
  static parseFilename(fullFilename) {
    const lastDotIndex = fullFilename.lastIndexOf(".");
    const filename = lastDotIndex === -1
      ? fullFilename
      : fullFilename.slice(0, lastDotIndex);
    const extension = lastDotIndex === -1
      ? ""
      : fullFilename.slice(lastDotIndex + 1);

    return {
      filename,
      extension
    };
  }

  /**
   * @param {Object} props
   */
  constructor(props) {
    super(props);
    this.handleSaveAuthentication = this.handleSaveAuthentication.bind(this);
    this.handleUploadCertificate = this.handleUploadCertificate.bind(this);
    this.handleCancelAuthentication = this.handleCancelAuthentication.bind(this);
    this.onUploadCertificateClick = this.onUploadCertificateClick.bind(this);

    this.fileInputRef = React.createRef();
    this.notificationRef = React.createRef();

    const clientHandle = props.client.handle;

    this.placeholdersMap = {
      [AUTH_TYPE_OAUTH]: {
        tokenUrl: "https://acme.com/rest/as/token.oauth2",
        redirectUri: authIntegrationName => `https://${clientHandle}.ada.support/api/oauth/${authIntegrationName}`,
        authUrl: "https://acme.com/as/authorization.oauth2"
      },
      [AUTH_TYPE_JWT]: {
        tokenUrl: "https://acme.com/rest/as/token.jwt",
        redirectUri: "https://acme.com/jwt/sign+in",
        authUrl: "https://acme.com/as/authorization.jwt"
      }
    };
  }

  /** Click on the Upload Certificate button */
  onUploadCertificateClick() {
    this.fileInputRef.current.click();
  }

  /**
   * Returns the correct placeholder depending on the auth type
   * @param {Immutable.Record} authentication
   * @param {String} fieldName
   * @returns {String}
   */
  getPlaceholder(authentication, fieldName) {
    const authType = authentication.get("authType");
    const authIntegrationName = authentication.get("name");
    const placeholders = this.placeholdersMap[authType];
    let placeholder = placeholders[fieldName];

    if (authType === AUTH_TYPE_OAUTH && fieldName === "redirectUri") {
      placeholder = placeholders[fieldName](authIntegrationName);
    }

    return placeholder;
  }

  /**
   * check if any input fields are dirty
   * @returns {Boolean}
   */
  isFieldDirty() {
    const { modal, authentication } = this.props;

    if (!modal.getIn(["modalProps", "newAuthentication"])) {
      return !authentication.equals(modal.getIn(["modalProps", "authentication"]));
    }

    return true;
  }

  /**
   * check if any input fields are empty
   * @returns {Boolean}
   */
  hasInvalidFields() {
    const { authentication } = this.props;

    return Boolean(authentication.get("invalidFields").size);
  }

  /**
   * check if save is disabled
   * @returns {Boolean}
   */
  isSaveDisabled() {
    return !this.isFieldDirty();
  }

  /**
   * handle cancel botton
   */
  handleCancelAuthentication() {
    const { setState } = this.props;
    setState("MODAL", {
      isOpen: false,
      view: ""
    });
  }

  /**
   * handle upload certificate
   * @param {Object} e
   */
  handleUploadCertificate(e) {
    const file = e.target.files[0];
    const reader = new FileReader();
    const { postCertificateAction } = this.props;
    const parsedFileName = AuthenticationModal.parseFilename(file.name);
    reader.onload = () => postCertificateAction(parsedFileName.filename, parsedFileName.extension, reader.result.split(",", 2)[1]);

    reader.readAsDataURL(file);
  }

  /**
   * handle save button
   */
  handleSaveAuthentication() {
    const { validateAuthenticationModalAction, updateOrCreateAuthenticationAction } = this.props;

    validateAuthenticationModalAction();
    updateOrCreateAuthenticationAction();
  }

  /**
   * render notice
   * @returns {ReactElement}
   */
  renderNotice() {
    return <ModalNotification noticeName="authentication" renderNotice={this.renderNoticeBody} />;
  }

  /**
   * @param {function(): void} onClose
   * @returns {ReactElement}
   */
  static renderNoticeBody({ onClose }) {
    return (
      <div className="ModalNotification__content">
        <SvgIcon
          icon="QuestionCircleFilled"
          customClassName="SettingsAuthenticationModal__notice__content__icon"
          height={20}
        />
        <div className=" SettingsAuthenticationModal__notice__content">
          Need help?&nbsp;
          <a
            href={notice.url}
            target="_blank"
            rel="noreferrer noopener"
            className="SettingsAuthenticationModal__notice__content__link"
          >
            Click here
          </a>
          &nbsp;to read our guide on how to set up authentication!
          <button
            className="SettingsAuthenticationModal__notice__content__dismiss"
            onClick={onClose}
            type="button"
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  /**
   * @returns {ReactElement}
   */
  render() {
    const {
      authentication,
      modal,
      handleUpdateAuthenticationModalAction,
      handleAddAuthenticationScopeAction,
      handleUpdateAuthenticationScopeAction,
      handleRemoveAuthenticationScopeAction,
      handleRemoveAuthenticationCertificateAction
    } = this.props;

    const validationErrors = Immutable.Map([
      "name",
      "tokenUrl",
      "redirectUri",
      "authUrl",
      "clientId",
      "clientSecret"
    ].map(field => (
      [field, authentication.invalidFields.includes(field) ||
      Boolean(authentication.errorMessages.get(field).size)]
    )));

    return (
      <div className="SettingsAuthenticationModal">
        {modal.getIn(["modalProps", "newAuthentication"]) ? (
          <h5 className="Modal__title">Create New Authentication</h5>
        ) : (
          <h5 className="Modal__title">Edit Authentication</h5>
        )}
        <div className="Modal__content SettingsAuthenticationModal__content">
          <ModalNotification noticeName="authentication" renderNotice={AuthenticationModal.renderNoticeBody}>
            <>
              <div
                className={classNames("SettingsAuthenticationModal__input-block g-form__block", {
                  "SettingsAuthenticationModal__input-block--invalid": validationErrors.get("name")
                })}
              >
                <div className="SettingsAuthenticationModal__name-type">
                  <div className="SettingsAuthenticationModal__name-type__Name">
                    <span className="g-input__label">Name</span>
                    <DebouncedInput
                      placeholder="Name your authentication"
                      value={authentication.name}
                      onChange={name => handleUpdateAuthenticationModalAction({ name })}
                      disabled={!modal.getIn(["modalProps", "newAuthentication"])}
                      isInvalid={validationErrors.get("name")}
                    />
                  </div>
                  <div className="SettingsAuthenticationModal__name-type__Type">
                    <span className="g-input__label">Type</span>
                    <Select
                      id="SettingsAuthenticationModal__input-type"
                      placeholder="OAuth 2"
                      options={authOptions}
                      value={authentication.authType}
                      onChange={authType => handleUpdateAuthenticationModalAction({ authType })}
                      disabled={!modal.getIn(["modalProps", "newAuthentication"])}
                    />
                  </div>
                </div>
                {Boolean(authentication.errorMessages.get("name").size) && (
                  <span className="SettingsAuthenticationModal__warning">
                    {authentication.errorMessages.get("name").first()}
                  </span>
                )}
              </div>
              <div
                className={classNames("SettingsAuthenticationModal__input-block g-form__block", {
                  "SettingsAuthenticationModal__input-block--invalid": validationErrors.get("tokenUrl")
                })}
              >
                <span className="g-input__label">Token URI</span>
                <DebouncedInput
                  placeholder={this.getPlaceholder(authentication, "tokenUrl")}
                  value={authentication.tokenUrl}
                  onChange={tokenUrl => handleUpdateAuthenticationModalAction({ tokenUrl })}
                  isInvalid={validationErrors.get("tokenUrl")}
                />
                {Boolean(authentication.errorMessages.get("tokenUrl").size) && (
                  <span className="SettingsAuthenticationModal__warning">
                    {authentication.errorMessages.get("tokenUrl").first()}
                  </span>
                )}
              </div>
              <div
                className={classNames("SettingsAuthenticationModal__input-block g-form__block", {
                  "SettingsAuthenticationModal__input-block--invalid": validationErrors.get("redirectUri")
                })}
              >
                <span className="g-input__label">Redirect URI</span>
                <DebouncedInput
                  placeholder={this.getPlaceholder(authentication, "redirectUri")}
                  value={authentication.redirectUri}
                  onChange={redirectUri => handleUpdateAuthenticationModalAction({ redirectUri })}
                  isInvalid={validationErrors.get("redirectUri")}
                />
                {Boolean(authentication.errorMessages.get("redirectUri").size) && (
                  <span className="SettingsAuthenticationModal__warning">
                    {authentication.errorMessages.get("redirectUri").first()}
                  </span>
                )}
              </div>
              <div
                className={classNames("SettingsAuthenticationModal__input-block g-form__block", {
                  "SettingsAuthenticationModal__input-block--invalid": validationErrors.get("authUrl")
                })}
              >
                <span className="g-input__label">Auth URI</span>
                <DebouncedInput
                  placeholder={this.getPlaceholder(authentication, "authUrl")}
                  value={authentication.authUrl}
                  onChange={authUrl => handleUpdateAuthenticationModalAction({ authUrl })}
                  isInvalid={validationErrors.get("authUrl")}
                />
                {Boolean(authentication.errorMessages.get("authUrl").size) && (
                  <span className="SettingsAuthenticationModal__warning">
                    {authentication.errorMessages.get("authUrl").first()}
                  </span>
                )}
              </div>
              {authentication.authType === AUTH_TYPE_OAUTH && (
                <div
                  className={classNames("SettingsAuthenticationModal__input-block g-form__block", {
                    "SettingsAuthenticationModal__input-block--invalid": validationErrors.get("clientId")
                  })}
                >
                  <span className="g-input__label">Client ID</span>
                  <DebouncedInput
                    placeholder="6779ef20e75817b79602"
                    value={authentication.clientId}
                    onChange={clientId => handleUpdateAuthenticationModalAction({ clientId })}
                    isInvalid={validationErrors.get("clientId")}
                  />
                  {Boolean(authentication.errorMessages.get("clientId").size) && (
                    <span className="SettingsAuthenticationModal__warning">
                      {authentication.errorMessages.get("clientId").first()}
                    </span>
                  )}
                </div>
              )}
              {authentication.authType === AUTH_TYPE_OAUTH && (
                <div
                  className={classNames("SettingsAuthenticationModal__input-block g-form__block", {
                    "SettingsAuthenticationModal__input-block--invalid": validationErrors.get("clientSecret")
                  })}
                >
                  <span className="g-input__label">Client Secret</span>
                  <DebouncedInput
                    placeholder="IwkOyQ4ckZBCbq8ecIgPfS3zSSzPgdhMpMiOmx"
                    value={authentication.clientSecret}
                    onChange={clientSecret => (
                      handleUpdateAuthenticationModalAction({ clientSecret })
                    )}
                    isInvalid={validationErrors.get("clientSecret")}
                  />
                  {Boolean(authentication.errorMessages.get("clientSecret").size) && (
                    <span className="SettingsAuthenticationModal__warning">
                      {authentication.errorMessages.get("clientSecret").first()}
                    </span>
                  )}
                </div>
              )}

              {authentication.authType === AUTH_TYPE_OAUTH && (
                <div className="SettingsAuthenticationModal__input-block g-form__block">
                  <div className="SettingsAuthenticationModal__scope-title">
                    <div className="SettingsAuthenticationModal__scope-title__wapper">
                      <span className="g-input__label">
                        <span>Scopes</span>
                        <span className="SettingsAuthenticationModal__optional-label">
                          {" "}
                          (Optional)
                        </span>
                      </span>
                    </div>
                    <Button
                      onClick={handleAddAuthenticationScopeAction}
                      title="Add Scope"
                      icon="CircleAddAlternate"
                      fillColor={colors.colorUIGood}
                      customClassName="SettingsAuthenticationModal__button-add-scope"
                      clear
                    />
                  </div>
                  <div>
                    {Boolean(authentication.scope.size) && (
                      <div id="SettingsAuthenticationModal__input-scope">
                        {authentication.scope.map((item, index) => (
                          // It's fine here
                          // eslint-disable-next-line react/no-array-index-key
                          <div key={index} className="SettingsAuthenticationModal__scope-list">
                            <div className="SettingsAuthenticationModal__scope-list__scope-row">
                              <div className="SettingsAuthenticationModal__scope-list__scope-row__row-item">
                                <DebouncedInput
                                  placeholder="Type a scope"
                                  value={item}
                                  onChange={(value) => {
                                    handleUpdateAuthenticationScopeAction({
                                      value,
                                      index
                                    });
                                  }}
                                />
                              </div>
                              <Button
                                onClick={() => {
                                  handleRemoveAuthenticationScopeAction({ index });
                                }}
                                title="Remove Scope"
                                icon="CircleRemoveAlternate"
                                fillColor={colors.colorUIBad}
                                customClassName={classNames(
                                  "SettingsAuthenticationModal__button-remove-scope",
                                  {
                                    "SettingsAuthenticationModal__button-remove-scope--first":
                                      authentication.scope.size === 1
                                  }
                                )}
                                clear
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {authentication.authType === AUTH_TYPE_OAUTH && (
                <div className="SettingsAuthenticationModal__input-block g-form__block">
                  <span className="g-input__label">
                    <span>Certificate</span>
                    <span className="SettingsAuthenticationModal__optional-label"> (Optional)</span>
                  </span>
                  {authentication.certificateName === "" ? (
                    <div className="SettingsAuthenticationModal__certificate-upload">
                      <div className="SettingsAuthenticationModal__certificate-upload__wrapper">
                        <Button
                          title="Upload certificate"
                          text="Upload certificate"
                          icon="Upload"
                          onClick={this.onUploadCertificateClick}
                          light
                        />
                        <input
                          type="file"
                          ref={this.fileInputRef}
                          onChange={this.handleUploadCertificate}
                          style={{ display: "none" }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="SettingsAuthenticationModal__uploaded-file-block">
                      <SvgIcon
                        height={20}
                        icon="Document"
                        fillColor={colors.colorGrey3Active}
                      />
                      <h3 className="SettingsAuthenticationModal__uploaded-file-block__name">
                        {authentication.certificateName}
                      </h3>
                      <Button
                        onClick={() => {
                          handleRemoveAuthenticationCertificateAction();
                        }}
                        icon="Clear"
                        fillColor={colors.colorGrey3Active}
                        clear
                      />
                    </div>
                  )}
                </div>
              )}
            </>
          </ModalNotification>
        </div>

        <div className="Modal__bottom SettingsAuthenticationModal__bottom">
          <Button
            onClick={this.handleCancelAuthentication}
            title="Cancel Authentication"
            text="Cancel"
            light
          />
          <Button
            onClick={this.handleSaveAuthentication}
            title="Save Authentication"
            text="Save"
            icon="Cloud"
            disabled={this.isSaveDisabled()}
            customClassName="SettingsAuthenticationModal__button-save"
          />
        </div>
      </div>
    );
  }
}

/**
 * @param {Object} state
 * @returns {Object}
 */
function mapState(state) {
  const {
    authenticationModal, modal, notices
  } = state;

  return {
    authentication: authenticationModal,
    modal,
    notices,
    client: getResource(state, "client")
  };
}

/**
 * @param {Object} dispatch
 * @returns {Object}
 */
function mapDispatch(dispatch) {
  return bindActionCreators(
    {
      postAuthenticationAction: postAuthentication,
      patchAuthenticationAction: patchAuthentication,
      handleUpdateAuthenticationModalAction: handleUpdateAuthenticationModal,
      handleAddAuthenticationScopeAction: handleAddAuthenticationScope,
      handleRemoveAuthenticationScopeAction: handleRemoveAuthenticationScope,
      handleRemoveAuthenticationCertificateAction: handleRemoveAuthenticationCertificate,
      handleUpdateAuthenticationScopeAction: handleUpdateAuthenticationScope,
      postCertificateAction: postCertificate,
      validateAuthenticationModalAction: validateAutenticationModal,
      updateOrCreateAuthenticationAction: updateOrCreateAuthentication
    },
    dispatch
  );
}

AuthenticationModal.propTypes = {
  client: PropTypes.instanceOf(Client).isRequired,
  /** modal from Redux */
  modal: ImmutablePropTypes.record.isRequired,
  /** validate the fields in the modal */
  validateAuthenticationModalAction: PropTypes.func.isRequired,
  /** set state */
  setState: PropTypes.func.isRequired,
  /** authenticationList from Redux */
  authentication: ImmutablePropTypes.record.isRequired,
  /** patch/post authentication and send to api */
  updateOrCreateAuthenticationAction: PropTypes.func.isRequired,
  /** handle update modal in Redux */
  handleUpdateAuthenticationModalAction: PropTypes.func.isRequired,
  /** handle add scope in Redux */
  handleAddAuthenticationScopeAction: PropTypes.func.isRequired,
  /** handle remove scope in Redux */
  handleRemoveAuthenticationScopeAction: PropTypes.func.isRequired,
  /** handle remove certificate in Redux */
  handleRemoveAuthenticationCertificateAction: PropTypes.func.isRequired,
  /** handle update scope in Redux */
  handleUpdateAuthenticationScopeAction: PropTypes.func.isRequired,
  /** handle post certificate and send to api */
  postCertificateAction: PropTypes.func.isRequired
};

const Connector = connect(
  mapState,
  mapDispatch
)(AuthenticationModal);

export default Connector;
